import React, { useState, useEffect } from 'react';
import { Form, Button, Card, Alert, Badge, Row, Col } from 'react-bootstrap';
import api from '../../utils/api';

const IntegrationsManager = () => {
  const API_URL = process.env.REACT_APP_API_URL;
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Spotify State
  const [spotifyClientId, setSpotifyClientId] = useState('');
  const [spotifyClientSecret, setSpotifyClientSecret] = useState('');
  const [showSpotify, setShowSpotify] = useState(false);
  const [spotifyConnected, setSpotifyConnected] = useState(false);

  // GHIN State
  const [ghinUsername, setGhinUsername] = useState('');
  const [ghinPassword, setGhinPassword] = useState('');
  const [ghinStats, setGhinStats] = useState({ handicap: '', rounds: 0, lastUpdated: null });
  const [showGhin, setShowGhin] = useState(false);
  const [ghinLoading, setGhinLoading] = useState(false);

  // Email State
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState(587);
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  const [notificationEmail, setNotificationEmail] = useState('');

  useEffect(() => {
    fetchSettings();
    
    const params = new URLSearchParams(window.location.search);
    if (params.get('spotify') === 'success') {
      setMessage('Spotify connected successfully!');
      setSpotifyConnected(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const fetchSettings = async () => {
    try {
      // Fetch Spotify
      const spotifyRes = await api.get('/spotify/config');
      setSpotifyClientId(spotifyRes.data.spotifyClientId || '');
      setSpotifyClientSecret(spotifyRes.data.spotifyClientSecret || '');
      setShowSpotify(spotifyRes.data.showSpotify || false);
      setSpotifyConnected(!!spotifyRes.data.spotifyRefreshToken);

      // Fetch GHIN
      const ghinRes = await api.get('/ghin/config');
      setGhinUsername(ghinRes.data.ghinUsername || '');
      setGhinStats({
          handicap: ghinRes.data.ghinHandicap,
          rounds: ghinRes.data.ghinRounds,
          lastUpdated: ghinRes.data.lastUpdated
      });
      setShowGhin(ghinRes.data.showGhin || false);

      // Fetch Email
      const emailRes = await api.get('/contact/config');
      setSmtpHost(emailRes.data.smtpHost);
      setSmtpPort(emailRes.data.smtpPort);
      setSmtpUser(emailRes.data.smtpUser);
      setNotificationEmail(emailRes.data.notificationEmail);

    } catch (err) {
      console.error(err);
    }
  };

  const handleSaveSpotify = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      await api.put('/spotify/config', {
        spotifyClientId,
        spotifyClientSecret,
        showSpotify,
      });
      setMessage('Spotify settings updated!');
    } catch (err) {
      setError('Failed to update Spotify settings.');
    }
  };

  const handleConnectSpotify = () => {
    window.location.href = `${API_URL}/api/spotify/auth`;
  };

  const handleSaveGhin = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    setGhinLoading(true);
    try {
      // Send manualSync: true to force a fetch if credentials are provided
      const { data } = await api.put('/ghin/config', {
        ghinUsername,
        ghinPassword,
        showGhin,
        manualSync: true 
      });
      
      setGhinStats({
          handicap: data.ghinHandicap,
          rounds: data.ghinRounds,
          lastUpdated: data.lastUpdated
      });
      
      setGhinPassword('');
      setMessage('GHIN settings updated & Synced!');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update GHIN settings. Check credentials.');
    } finally {
        setGhinLoading(false);
    }
  };

  const handleSaveEmail = async (e) => {
    e.preventDefault();
    setMessage(null);
    setError(null);
    try {
      await api.put('/contact/config', {
        smtpHost,
        smtpPort,
        smtpUser,
        smtpPass,
        notificationEmail,
      });
      setSmtpPass(''); // Clear password from state for security
      setMessage('Email settings updated!');
    } catch (err) {
      setError('Failed to update Email settings.');
    }
  };

  return (
    <div className="d-flex justify-content-center">
      <Card style={{ width: '100%', maxWidth: '800px' }}>
        <Card.Body>
          <h3 className="mb-4">Integrations</h3>
          {message && <Alert variant="success">{message}</Alert>}
          {error && <Alert variant="danger">{error}</Alert>}

          {/* SPOTIFY SECTION */}
          <div className="mb-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Spotify</h4>
              {spotifyConnected ? <Badge bg="success">Connected</Badge> : <Badge bg="secondary">Not Connected</Badge>}
            </div>
            <hr />
            <Form onSubmit={handleSaveSpotify}>
              <Form.Group className="mb-3">
                <Form.Label>Client ID</Form.Label>
                <Form.Control 
                  type="text" 
                  value={spotifyClientId}
                  onChange={(e) => setSpotifyClientId(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Client Secret</Form.Label>
                <Form.Control 
                  type="password" 
                  value={spotifyClientSecret}
                  onChange={(e) => setSpotifyClientSecret(e.target.value)}
                />
              </Form.Group>
              <Form.Check 
                type="switch"
                label="Show Spotify Stats"
                checked={showSpotify}
                onChange={(e) => setShowSpotify(e.target.checked)}
                className="mb-3"
              />
              <div className="d-flex gap-2">
                <Button variant="primary" type="submit">Save Spotify</Button>
                <Button 
                  variant="success" 
                  type="button" 
                  onClick={handleConnectSpotify}
                  disabled={!spotifyClientId}
                >
                  {spotifyConnected ? 'Reconnect' : 'Connect'}
                </Button>
              </div>
            </Form>
          </div>

          {/* GHIN SECTION */}
          <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">GHIN Handicap</h4>
              {ghinStats.lastUpdated && (
                  <small className="text-muted">Last Synced: {new Date(ghinStats.lastUpdated).toLocaleDateString()}</small>
              )}
            </div>
            <hr />
            <Form onSubmit={handleSaveGhin}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>GHIN Number or Email</Form.Label>
                    <Form.Control 
                      type="text" 
                      placeholder="GHIN Number or Email"
                      value={ghinUsername}
                      onChange={(e) => setGhinUsername(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control 
                      type="password" 
                      placeholder={ghinStats.handicap ? "(Unchanged)" : "GHIN Login Password"}
                      value={ghinPassword}
                      onChange={(e) => setGhinPassword(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              
              {ghinStats.handicap && (
                  <div className="alert alert-secondary py-2 mb-3">
                      <strong>Current Status:</strong> {ghinStats.handicap} Index | {ghinStats.rounds} Rounds (YTD)
                  </div>
              )}

              <Form.Check 
                type="switch"
                label="Show GHIN Stats"
                checked={showGhin}
                onChange={(e) => setShowGhin(e.target.checked)}
                className="mb-3"
              />
              
              <Button variant="primary" type="submit" disabled={ghinLoading}>
                  {ghinLoading ? 'Syncing...' : 'Save & Sync GHIN'}
              </Button>
            </Form>
          </div>

          {/* EMAIL SECTION */}
          <div className="mt-5">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="mb-0">Email (SMTP)</h4>
            </div>
            <hr />
            <Form onSubmit={handleSaveEmail}>
              <Row>
                <Col md={8}>
                  <Form.Group className="mb-3">
                    <Form.Label>SMTP Host</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={smtpHost}
                      onChange={(e) => setSmtpHost(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Port</Form.Label>
                    <Form.Control 
                      type="number" 
                      value={smtpPort}
                      onChange={(e) => setSmtpPort(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SMTP Username</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={smtpUser}
                      onChange={(e) => setSmtpUser(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>SMTP Password</Form.Label>
                    <Form.Control 
                      type="password" 
                      placeholder={smtpUser ? "(Unchanged)" : ""}
                      value={smtpPass}
                      onChange={(e) => setSmtpPass(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Form.Group className="mb-3">
                <Form.Label>Notification Email (Where to receive submissions)</Form.Label>
                <Form.Control 
                  type="email" 
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                />
              </Form.Group>
              <Button variant="primary" type="submit">Save Email Settings</Button>
            </Form>
          </div>

        </Card.Body>
      </Card>
    </div>
  );
};

export default IntegrationsManager;
